import { Alert, Box, Button, Chip, Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getLatestPubSLEvent } from "../../api/seniority";
import { selectJobCodeData } from "../../slices/commonConfig/commonConfigSlice";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { getEmployeeStationList } from "../../util/commonUtils";
import { CONTRACT_SENIORITY_LIST_TYPE_MAPPER } from "../../util/constants";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";

const DashboardEmployeeSeniorityListCard = (props) => {
  const userSession = useSelector(selectUserSession);
  const jobCodeData = useSelector(selectJobCodeData);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [seniorityListData, setSeniorityListData] = useState([]);

  useEffect(() => {
    fetchSeniorityListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSeniorityListData = async () => {
    try {
      const stationParam = userSession.activeStation;
      const userWorkgroup = getWorkgroupGivenJobCode(userSession.jobCode);
      const jobCode = userSession.jobCode;
      const userContract = jobCodeData[jobCode].unionContract;
      const seniorityListTypes = CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userContract, stationParam);

      const userStations = getEmployeeStationList(userSession);
      let slData = [];
      for (const station of userStations) {
        for (const seniorityListType of seniorityListTypes) {
          const response = await getLatestPubSLEvent(station, userWorkgroup, seniorityListType);
          const data = await response.json();
          const empSlRecord = data?.seniorityList?.find((item) => item?.eid === userSession?.eid);
          const rank = empSlRecord ? empSlRecord.rank : null;
          slData.push({
            station: station,
            seniorityListType: seniorityListType,
            isPublished: !!data.publishedBy,
            rank: rank,
            totalEmployees: data.fullTimeCount + data.partTimeCount,
            publishedBy: data?.publishedBy,
            createdAt: data.createdAt
              ? getStationTimeZoneDateTimeStringFromUTCInteger(data.createdAt, userSession.activeStation).toFormat(
                  "MM/dd/yyyy T ZZZZ"
                )
              : null,
            isTemp: userSession.tempStation ? userSession.tempStation === station : false,
          });
        }
      }

      setSeniorityListData(slData);
    } catch (e) {
      console.error(e);
      setFetchError("Unexpected Error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderSeniorityListTitle = (data) => {
    const renderedStation = data.isTemp ? ` - ${data.station}` : ``;
    return (
      <Stack direction="row" alignItems="center" height={30}>
        <Typography variant="body2">{`${data.seniorityListType.toUpperCase()}${renderedStation}`}</Typography>
        {data.isTemp && <Chip component="p" label="Temp" size="small" sx={{ marginLeft: "10px" }} />}
      </Stack>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        backgroundColor: "secondary.swaGray1",
        borderRadius: "5px",
        display: "flex",
        flexDirection: "column",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
        paddingTop: "1rem",
        paddingBottom: "1rem",
      }}
    >
      <Grid
        container
        sx={{
          textAlign: "left",
          marginBottom: ".5rem",
        }}
      >
        <Grid item xs={6}>
          <Typography variant="h6" component="div" style={{ textAlign: "left", fontWeight: "bold" }}>
            My Seniority List
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "end" }}>
          <Button href={`#/mySeniorityList`} size="small">
            <Typography sx={{ color: "primary.swaBlue", textDecoration: "underline" }}>See All</Typography>
            <Typography sx={{ color: "primary.swaBlue" }}>&rarr;</Typography>
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>

      {isLoading ? (
        <Skeleton height={80} />
      ) : fetchError !== "" ? (
        <Alert
          variant="filled"
          severity="error"
          sx={{ display: "flex", width: "200px", alignSelf: "center", margin: "1rem" }}
        >
          {fetchError}
        </Alert>
      ) : (
        seniorityListData.map((data, idx) => {
          return (
            <Box key={`${data.seniorityListType}-${data.rank}`}>
              {!data.isPublished ? (
                <Grid container sx={{ textAlign: "left" }}>
                  <Grid item xs={12}>
                    {renderSeniorityListTitle(data)}
                    <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>Unpublished</Typography>
                  </Grid>
                </Grid>
              ) : data.rank ? (
                <Grid container sx={{ textAlign: "left", alignItems: "center" }}>
                  <Grid item xs={5}>
                    {renderSeniorityListTitle(data)}
                    <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>
                      Rank #{data.rank} of {data.totalEmployees}
                    </Typography>
                  </Grid>
                  <Grid item xs={7} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">Last published by {data.publishedBy}</Typography>
                    <Typography variant="body2">{data.createdAt}</Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container sx={{ textAlign: "left", alignItems: "center" }}>
                  <Grid item xs={5}>
                    {renderSeniorityListTitle(data)}
                    <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>Not in list</Typography>
                  </Grid>
                  <Grid item xs={7} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">Last published by {data.publishedBy}</Typography>
                    <Typography variant="body2">{data.createdAt}</Typography>
                  </Grid>
                </Grid>
              )}
              {idx !== seniorityListData.length - 1 && <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />}
            </Box>
          );
        })
      )}
    </Paper>
  );
};

export default DashboardEmployeeSeniorityListCard;
